const baseFields = {
  requisite: 'Requisite',
  title: 'Title',
  type: 'Type',
  sum: 'Sum',
  vat: 'Calculation procedure',
  suggestions: {
    inn: 'INN of your company',
    bik: 'BIK or bank name',
    fio: 'Full name of the signer',
    address: 'Legal address of the company'
  },
  contact: {
    title: 'Contact',
    firstName: 'First name',
    middleName: 'Middle name',
    lastName: 'Last name',
    email: 'E-mail',
    phone: 'Phone',
    matches: 'Matches the data specified in the profile of your personal account'
  },
  signer: {
    position: {
      nominative: 'Position',
      genitive: 'Position'
    },
    basis: {
      nominative: 'Acts on basis of',
      genitive: 'Acts on basis of'
    },
    fullName: {
      nominative: 'Full name of the signer',
      genitive: 'Full name of the signer'
    }
  },
  data: {
    legal: {
      businessType: 'Company type',
      name: 'Name of company',
      opf: 'OPF',
      ogrn: 'OGRN',
      inn: 'INN',
      kpp: 'KPP',
      okpo: 'OKPO'
    },
    bank: {
      name: 'Name of bank',
      rs: 'Checking account',
      ks: 'Corr. account',
      bik: 'BIK'
    },
    tax: {
      system: 'Tax system',
      vat: 'The company works with VAT'
    },
    legalAddress: {
      title: 'Legal address',
      country: 'Country',
      city: 'City',
      index: 'Index',
      address: 'Address',
      addressMatches: 'Matches with legal address',
      phone: 'Phone',
      phoneAdd: 'Extension number'
    },
    postalAddress: {
      title: 'Postal address',
      country: 'Country',
      city: 'City',
      index: 'Index',
      address: 'Address',
      addressMatches: 'Matches with legal address',
      phone: 'Phone',
      phoneAdd: 'Extension number'
    },
    personal: {
      firstName: 'First name',
      middleName: 'Middle name',
      lastName: 'Last name',
      document: {
        series: 'Document series',
        number: 'Document number',
        issuer: 'Issuer',
        issuerCode: 'Issuer code',
        issueDate: 'Issue date',
        registrationAddress: 'Registration address',
        registrationDate: 'Registration date',
        citizenship: 'Citizenship'
      }
    }
  },
  moderation: 'Moderation',
  documents: {
    templates: {
      shared: 'Available templates',
      assigned: 'Assigned template',
      purpose: 'Purpose',
      purposes: {
        bill: 'Bill',
        contract: 'Primary contract',
        contractAnnex1: 'Contract annex #1',
        addAgreementCreditPayment: 'Supplementary agreement on credit payment method',
        invoice: 'Invoice',
        UTD: 'UTD',
        UTDplus: 'UTD plus',
        Letter1: 'Letter 1',
        Letter2: 'Letter 2',
        Letter3: 'Letter 3'
      }
    }
  },
  country: 'Country',
  city: 'City',
  index: 'Index',
  address: 'Address',
  addressMatches: 'Matches with legal address',
  phone: 'Phone',
  phoneAdd: 'Extension number'
}

export default {
  title: 'Requisites',

  titles: {
    create: 'Create new requisites',
    autoFilling: 'Autocomplete requisites',
    document: {
      templates: 'Templates'
    },
    empty: {
      title: 'No requisites created',
      sub: 'Create a new requisite using the button below'
    },
    generate: {
      document: 'Download document'
    },
    requisites: 'Requisites',
    notRequired: 'Optional part'
  },

  dialogs: {
    titles: {
      create: 'Create requisites',
      update: 'Update requisites',
      remove: 'Remove requisites'
    }
  },

  headers: {
    ...baseFields,

    businessInfo: 'INN and KPP',
    contactInfo: 'Contacts'
  },

  labels: {
    ...baseFields
  },

  subheaders: {
    title: 'Title',
    contact: 'Responsible person for signing the contract',
    signer: 'Signer',
    general: 'General',
    legal: 'Legal',
    bank: 'Bank',
    tax: 'Tax',
    personal: 'Personal',
    document: 'Document',
    legalAddress: 'Legal address',
    postalAddress: 'Postal address',
    documents: {
      templates: {
        shared: 'Shared templates',
        assigned: 'Assigned templates'
      }
    }
  },

  contents: {
    documents: {
      templates: {
        shared: 'Document templates that the reseller can attach to users',
        assigned: 'Document templates that will be used to generate the user\'s documents'
      }
    },
    confirm: {
      remove: 'Confirm requisites removal.'
    }
  },

  types: {
    business: 'Legal entity',
    personal: 'Individual'
  },

  errors: {
    ogrn: 'Must be from 13 to 15 digits',
    inn: 'Must be from 10 to 12 digits',
    kpp: 'Must be 9 digits',
    okpo: 'Must be 8 digits',
    rs: 'Must be 20 digits',
    ks: 'Must be 20 digits',
    bik: 'Must be 9 digits',
    index: 'Must be 6 digits',
    document: {
      number: 'Must be 6 digits'
    },
    documentsNoRequisitesFound: 'No requisites found',
    documentsNoDocumentTemplateFound: 'No document template found',
    contact: {
      phone: 'The phone must be active',
      email: 'Enter a valid e-mail'
    }
  },

  hints: {
    title: 'Title of requisite for quick search on the platform when invoicing.',
    required: 'Fields marked with * are required',
    owner: 'Select the user for whom you want to create requisites',
    suggestion: 'This section will help you save time filling out the requisites. Just enter the required data in each field and select the appropriate option from the list.',
    suggestions: {
      inn: 'Enter INN to search for requisites',
      bik: 'Enter the BIK or the first letters of the bank name',
      fio: 'Enter the full name of the general director or manager',
      address: 'Enter an address to search'
    },
    data: {
      legal: {
        name: 'Without quotes and legal form. For example, if your company is LLC "Landyshe", enter only Landyshe',
        opf: 'Legal form (you can write any, not only from the list)',
        ogrn: 'Primary state registration number',
        inn: 'Taxpayer Identification Number',
        kpp: 'Reason code for registration',
        okpo: 'All-Russian Classifier of Enterprises and Organizations'
      },
      bank: {
        name: 'Bank name',
        rs: 'Bank account',
        ks: 'Correspondent account',
        bik: 'Bank Identification Code'
      }
    },
    nominative: 'Nominative',
    genitive: 'Genitive',
    sum: 'Minimal sum 100 rubles',
    address: 'Street, house, building, office / apartment',
    validation: {
      state: {
        true: 'Filled',
        false: 'Errors',
        undefined: 'Not filled'
      }
    }
  },

  vat: {
    true: 'With VAT',
    false: 'Without VAT'
  },

  buttons: {
    add: 'Add requisites',
    Edit: 'Edit requisites',
    document: {
      templates: 'User document templates'
    }
  },

  snackbars: {
    created: 'Requisites created successfully.',
    updated: 'Requisites updated successfully.',
    removed: 'Requisites removed successfully.'
  },

  tooltips: {
    isActive: 'Active or inactive requisite. If the requisite is inactive, you will not be able to use it for invoicing.',
    favorites: 'Make the requisite a favorite',
    filter: {
      isActive: 'Show deactivated requisites.'
    },
    icons: {
      isActive: 'Requisite is active',
      notActive: 'Requisite not active'
    }
  }
}
